// ========================================
// 【ナナ文具 カスタムJS】
// ファイル名: nana-quantity-selector.js
// 用途: 商品ページの数量入力をドロップダウン形式に変更
//       在庫数に応じて選択肢の上限を自動調整（最大30個）
//       バリエーション切り替え時に選択肢をリアルタイム更新
//
// ⚠️ テーマ更新後の復元手順 ⚠️
//
// 【復元箇所1: JSの読み込み】
// sections/main-product.liquid の冒頭付近
// product-form.js の読み込みの直後に以下を追加：
//
//   <script src="{{ 'nana-quantity-selector.js' | asset_url }}" defer="defer"></script>
//
// 【復元箇所2: CSSの確認】
// スタイルは assets/nana-common.css に記述済み（.nana-quantity-selector）
// nana-common.css はテーマ更新で上書きされないため、通常は対応不要
//
// 【復元箇所3: HTMLブロックの追加】
// sections/main-product.liquid の quantity_selector ブロック内に
// 以下のHTMLを復元する：
//
// {%- comment -%} custom ここからセレクトボックス追加コード {%- endcomment -%}
// {%- when 'quantity_selector' -%}
// <div
//   id="Quantity-Form-{{ section.id }}"
//   class="product-form__input product-form__quantity{% if settings.inputs_shadow_vertical_offset != 0 and settings.inputs_shadow_vertical_offset < 0 %} product-form__quantity-top{% endif %}"
//   {{ block.shopify_attributes }}
// >
//   {%- assign cart_qty = cart | item_count_for_variant: product.selected_or_first_available_variant.id -%}
//   <span class="visually-hidden" id="quantity-label-{{ section.id }}">
//     {%- if cart_qty > 0 -%}
//       {{- 'products.product.quantity.in_cart_aria_label' | t: quantity: cart_qty -}}
//     {%- else -%}
//       {{- 'products.product.quantity.label' | t -}}
//     {%- endif -%}
//   </span>
//   <div class="nana-quantity-selector">
//     <label class="quantity__label form__label" for="Quantity-{{ section.id }}" aria-labelledby="quantity-label-{{ section.id }}">
//       {{ 'products.product.quantity.label' | t }}
//     </label>
//     <div class="quantity__select-wrapper">
//       <nana-quantity-selector class="quantity" data-url="{{ product.url }}" data-section="{{ section.id }}">
//         <select
//           class="quantity__input"
//           name="quantity"
//           id="Quantity-{{ section.id }}"
//           data-cart-quantity="{{ cart_qty }}"
//           data-min="{{ product.selected_or_first_available_variant.quantity_rule.min }}"
//           {% if product.selected_or_first_available_variant.quantity_rule.max != null %}
//             data-max="{{ product.selected_or_first_available_variant.quantity_rule.max }}"
//           {% endif %}
//           form="{{ product_form_id }}"
//         >
//           {%- assign inventory_qty = product.selected_or_first_available_variant.inventory_quantity -%}
//           {%- assign max_qty = 30 -%}
//           {%- if product.selected_or_first_available_variant.inventory_management == 'shopify' -%}
//             {%- if inventory_qty > 0 and inventory_qty < max_qty -%}
//               {%- assign max_qty = inventory_qty -%}
//             {%- endif -%}
//           {%- endif -%}
//           {%- if product.selected_or_first_available_variant.quantity_rule.max != null -%}
//             {%- assign max_qty = product.selected_or_first_available_variant.quantity_rule.max -%}
//           {%- endif -%}
//           {%- for i in (1..max_qty) -%}
//             <option value="{{ i }}" {% if i == product.selected_or_first_available_variant.quantity_rule.min %}selected{% endif %}>
//               {{ i }}
//             </option>
//           {%- endfor -%}
//         </select>
//       </nana-quantity-selector>
//     </div>
//     <span class="quantity__rules-cart{% if cart_qty == 0 %} hidden{% endif %}" style="font-size: 12px; color: rgba(var(--color-foreground), 0.75);">
//       {%- render 'loading-spinner' -%}
//       <span>({{ 'products.product.quantity.in_cart_html' | t: quantity: cart_qty }})</span>
//     </span>
//   </div>
//   <div class="price-per-item__container">
//   {%- comment -%} custom ここまでセレクトボックスに変更 {%- endcomment -%}
//
// ========================================

class NanaQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.selectElement = this.querySelector('select');
    this.productForm = this.closest('product-form') || this.closest('form');
  }

  connectedCallback() {
    // バリエーション変更を監視
    if (this.productForm) {
      this.productForm.addEventListener('variant:change', this.handleVariantChange.bind(this));
    }
    
    // DOMから直接在庫情報を取得する方法も追加
    this.observeInventoryChanges();
  }

  observeInventoryChanges() {
    // 在庫表示エリアを監視
    const inventoryElement = document.querySelector('[id^="Inventory-"]');
    if (inventoryElement) {
      const observer = new MutationObserver(() => {
        this.updateFromDOM();
      });
      observer.observe(inventoryElement, { childList: true, subtree: true });
    }
  }

  handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || !this.selectElement) return;

    // まずイベントデータから更新を試みる
    this.updateQuantityOptions(variant);
    
    // 少し待ってからDOMからも確認（フォールバック）
    setTimeout(() => {
      this.updateFromDOM();
    }, 100);
  }

  updateFromDOM() {
    // DOMから在庫情報を取得
    const inventoryElement = document.querySelector('[id^="Inventory-"]');
    if (!inventoryElement) return;

    const inventoryText = inventoryElement.textContent;
    let maxQty = 30; // デフォルト上限

    // 「残り○個」のようなテキストから数字を抽出
    const match = inventoryText.match(/(\d+)/);
    if (match) {
      const inventoryQty = parseInt(match[1]);
      if (inventoryQty > 0 && inventoryQty < maxQty) {
        maxQty = inventoryQty;
      }
    }

    // 「在庫切れ」の場合
    if (inventoryText.includes('在庫切れ') || inventoryText.includes('Out of stock')) {
      maxQty = 0;
    }

    // 選択肢を更新
    this.rebuildOptions(1, maxQty);
  }

  updateQuantityOptions(variant) {
    let maxQty = 30; // デフォルト上限
    const minQty = variant.quantity_rule?.min || 1;

    // quantity_rule.max が設定されている場合
    if (variant.quantity_rule?.max) {
      maxQty = variant.quantity_rule.max;
    }

    // 在庫管理がオンの場合（イベントデータに含まれている場合）
    if (variant.inventory_management === 'shopify') {
      if (variant.inventory_quantity !== undefined && variant.inventory_quantity > 0) {
        if (variant.inventory_quantity < maxQty) {
          maxQty = variant.inventory_quantity;
        }
      }
    }

    // 在庫切れの場合
    if (variant.available === false) {
      maxQty = 0;
    }

    this.rebuildOptions(minQty, maxQty);
  }

  rebuildOptions(minQty, maxQty) {
    if (!this.selectElement) return;

    // 現在の選択値を保持
    const currentValue = parseInt(this.selectElement.value);

    // 在庫切れの場合
    if (maxQty === 0) {
      this.selectElement.innerHTML = '<option value="0">在庫切れ</option>';
      this.selectElement.disabled = true;
      return;
    }

    // 選択肢を再生成
    this.selectElement.disabled = false;
    this.selectElement.innerHTML = '';

    for (let i = minQty; i <= maxQty; i++) {
      const option = document.createElement('option');
      option.value = i;
      option.textContent = i;
      
      // 以前の選択値を維持（範囲内の場合）
      if (i === currentValue && currentValue <= maxQty && currentValue >= minQty) {
        option.selected = true;
      } else if (i === minQty && (currentValue > maxQty || currentValue < minQty)) {
        option.selected = true;
      }
      
      this.selectElement.appendChild(option);
    }

    // data属性を更新
    this.selectElement.dataset.min = minQty;
    this.selectElement.dataset.max = maxQty;
  }
}

customElements.define('nana-quantity-selector', NanaQuantitySelector);